import {
  ArrowUpRight,
  Calendar,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  Clock,
  Download,
  IndianRupee,
  Receipt,
  RefreshCw,
  Search,
  TrendingDown,
  TrendingUp,
  X,
  XCircle,
} from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import API from "../../lib/api";

const PAGE_SIZE = 20;

const STATUS_META = {
  completed: {
    label: "Completed",
    color: "bg-green-100 text-green-700",
    icon: CheckCircle2,
  },
  pending: {
    label: "Pending",
    color: "bg-amber-100 text-amber-700",
    icon: Clock,
  },
  failed: { label: "Failed", color: "bg-red-100 text-red-600", icon: XCircle },
};

const PIE_COLORS = ["#10b981", "#f59e0b", "#ef4444"];

function formatINR(n) {
  if (n == null) return "–";
  const num = Number(n);
  if (num >= 1e7) return `₹${(num / 1e7).toFixed(2)} Cr`;
  if (num >= 1e5) return `₹${(num / 1e5).toFixed(2)} L`;
  if (num >= 1e3) return `₹${(num / 1e3).toFixed(1)} K`;
  return `₹${num.toLocaleString("en-IN")}`;
}

function formatINRFull(n) {
  return `₹${Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 2 })}`;
}

function formatBucketLabel(bucket, granularity) {
  if (granularity === "monthly") {
    const [y, m] = bucket.split("-");
    const d = new Date(Number(y), Number(m) - 1, 1);
    return d.toLocaleDateString("en-IN", { month: "short", year: "2-digit" });
  }
  return bucket;
}

export default function PaymentsPanel() {
  const [insights, setInsights] = useState(null);
  const [loadingInsights, setLoadingInsights] = useState(true);
  const [insightsError, setInsightsError] = useState("");

  const [granularity, setGranularity] = useState("monthly");

  const [filters, setFilters] = useState({
    search: "",
    status: "",
    from: "",
    to: "",
  });
  const [appliedFilters, setAppliedFilters] = useState({
    search: "",
    status: "",
    from: "",
    to: "",
  });
  const [page, setPage] = useState(1);
  const [list, setList] = useState({
    payments: [],
    total: 0,
    page: 1,
    pages: 1,
    totals: {},
  });
  const [loadingList, setLoadingList] = useState(true);
  const [listError, setListError] = useState("");

  const loadInsights = useCallback(async () => {
    setLoadingInsights(true);
    setInsightsError("");
    try {
      const { data } = await API.get("/admin/payments/insights");
      setInsights(data);
    } catch (e) {
      setInsightsError(
        e.response?.data?.detail || e.message || "Failed to load insights",
      );
    }
    setLoadingInsights(false);
  }, []);

  const loadList = useCallback(async () => {
    setLoadingList(true);
    setListError("");
    try {
      const params = { page, limit: PAGE_SIZE };
      if (appliedFilters.search) params.search = appliedFilters.search;
      if (appliedFilters.status) params.status = appliedFilters.status;
      if (appliedFilters.from) params.from = appliedFilters.from;
      if (appliedFilters.to) params.to = appliedFilters.to + " 23:59:59";
      const { data } = await API.get("/admin/payments", { params });
      setList(data);
    } catch (e) {
      setListError(
        e.response?.data?.detail || e.message || "Failed to load payments",
      );
    }
    setLoadingList(false);
  }, [page, appliedFilters]);

  useEffect(() => {
    loadInsights();
  }, [loadInsights]);
  useEffect(() => {
    loadList();
  }, [loadList]);

  const applyFilters = () => {
    setPage(1);
    setAppliedFilters({ ...filters });
  };
  const resetFilters = () => {
    setFilters({ search: "", status: "", from: "", to: "" });
    setAppliedFilters({ search: "", status: "", from: "", to: "" });
    setPage(1);
  };

  const series = useMemo(() => {
    if (!insights) return [];
    const raw = insights.series[granularity] || [];
    return raw.map((r) => ({
      label: formatBucketLabel(r.bucket, granularity),
      revenue: r.revenue,
      count: r.count,
    }));
  }, [insights, granularity]);

  const statusPie = useMemo(() => {
    if (!insights) return [];
    const c = insights.counts;
    return [
      { name: "Completed", value: c.completed },
      { name: "Pending", value: c.pending },
      { name: "Failed", value: c.failed },
    ].filter((d) => d.value > 0);
  }, [insights]);

  const exportCsv = () => {
    const header = [
      "ID",
      "Order",
      "Status",
      "Amount",
      "Currency",
      "Method",
      "Payment ID",
      "User",
      "Email",
      "Service",
      "Paid At",
    ];
    const rows = list.payments.map((p) => [
      p.id,
      p.order_id,
      p.status,
      p.amount,
      p.currency,
      p.payment_method || "",
      p.payment_id || "",
      p.user_name,
      p.user_email,
      p.service_name || "",
      p.paid_at ? new Date(p.paid_at).toISOString() : "",
    ]);
    const csv = [header, ...rows]
      .map((r) =>
        r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `payments-page-${page}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6">
      {/* Section: KPIs */}
      {loadingInsights ? (
        <div className="flex items-center justify-center py-16">
          <div className="w-7 h-7 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
        </div>
      ) : insightsError ? (
        <div className="bg-white rounded-2xl border border-red-200 p-5">
          <p className="text-sm text-red-600 font-body">{insightsError}</p>
          <button
            onClick={loadInsights}
            className="mt-2 text-xs text-[#6C63FF] underline font-body"
          >
            Retry
          </button>
        </div>
      ) : insights ? (
        <>
          <KPISection insights={insights} onRefresh={loadInsights} />
          <ChartsSection
            insights={insights}
            granularity={granularity}
            setGranularity={setGranularity}
            series={series}
            statusPie={statusPie}
          />
          <ServiceCardsGrid insights={insights} />
          <SecondaryRow insights={insights} />
        </>
      ) : null}

      {/* Section: Payments list */}
      <div>
        <div className="flex items-center gap-2 mb-3">
          <Receipt className="h-4 w-4 text-[#6C63FF]" />
          <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">
            All Payments
          </h3>
        </div>

        <div className="bg-white rounded-xl border border-[#E8E6F8] p-4 mb-4 grid grid-cols-1 md:grid-cols-12 gap-3 items-end">
          <div className="md:col-span-4 relative">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              Search
            </label>
            <Search className="absolute left-3 top-[34px] h-3.5 w-3.5 text-[#9CA3AF]" />
            <input
              type="text"
              placeholder="User, email, order #, payment id…"
              value={filters.search}
              onChange={(e) =>
                setFilters((f) => ({ ...f, search: e.target.value }))
              }
              onKeyDown={(e) => e.key === "Enter" && applyFilters()}
              className="w-full pl-9 pr-3 py-2 text-sm font-body border border-[#E8E6F8] rounded-lg bg-[#F5F4FB] text-[#1A1A2E] placeholder:text-[#9CA3AF] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              Status
            </label>
            <select
              value={filters.status}
              onChange={(e) =>
                setFilters((f) => ({ ...f, status: e.target.value }))
              }
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            >
              <option value="">All</option>
              <option value="completed">Completed</option>
              <option value="pending">Pending</option>
              <option value="failed">Failed</option>
            </select>
          </div>

          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              From
            </label>
            <input
              type="date"
              value={filters.from}
              onChange={(e) =>
                setFilters((f) => ({ ...f, from: e.target.value }))
              }
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>
          <div className="md:col-span-2">
            <label className="block text-[10px] uppercase tracking-wide font-body text-[#9CA3AF] mb-1">
              To
            </label>
            <input
              type="date"
              value={filters.to}
              onChange={(e) =>
                setFilters((f) => ({ ...f, to: e.target.value }))
              }
              className="w-full text-sm font-body border border-[#E8E6F8] rounded-lg px-3 py-2 bg-[#F5F4FB] text-[#1A1A2E] focus:outline-none focus:ring-1 focus:ring-[#6C63FF]/50"
            />
          </div>

          <div className="md:col-span-2 flex gap-2">
            <button
              onClick={applyFilters}
              className="flex-1 px-3 py-2 bg-[#6C63FF] hover:bg-[#5B52CC] text-white text-xs font-body font-semibold rounded-lg transition-colors"
            >
              Apply
            </button>
            <button
              onClick={resetFilters}
              className="px-3 py-2 border border-[#E8E6F8] text-xs font-body text-[#6B7280] hover:bg-[#F5F4FB] rounded-lg transition-colors"
              title="Reset"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          </div>
        </div>

        {/* Filtered totals strip */}
        {!loadingList && !listError && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-4">
            <MiniStat
              label="Filtered Revenue"
              value={formatINR(list.totals?.revenue)}
              sub={`${list.totals?.completed_count ?? 0} completed`}
              tone="text-green-600"
            />
            <MiniStat
              label="Pending"
              value={list.totals?.pending_count ?? 0}
              sub="payments"
              tone="text-amber-600"
            />
            <MiniStat
              label="Failed"
              value={list.totals?.failed_count ?? 0}
              sub="payments"
              tone="text-red-500"
            />
            <MiniStat
              label="Result"
              value={list.total}
              sub="rows in filter"
              tone="text-[#6C63FF]"
            />
          </div>
        )}

        <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm overflow-hidden">
          {loadingList ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-6 h-6 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : listError ? (
            <div className="p-6 text-center">
              <p className="text-sm text-red-500 font-body">{listError}</p>
              <button
                onClick={loadList}
                className="mt-2 text-xs text-[#6C63FF] underline font-body"
              >
                Retry
              </button>
            </div>
          ) : list.payments.length === 0 ? (
            <p className="text-center text-[#9CA3AF] text-sm font-body py-12">
              No payments match your filters
            </p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm font-body">
                <thead>
                  <tr className="bg-[#F5F4FB] border-b border-[#E8E6F8]">
                    {[
                      "Date",
                      "Order",
                      "User",
                      "Service",
                      "Amount",
                      "Method",
                      "Status",
                      "Payment ID",
                    ].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-heading font-semibold text-[#6B7280] whitespace-nowrap"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#E8E6F8]">
                  {list.payments.map((p) => {
                    const meta = STATUS_META[p.status] || {
                      label: p.status,
                      color: "bg-gray-100 text-gray-600",
                      icon: Clock,
                    };
                    const StatusIcon = meta.icon;
                    const date = p.paid_at || p.created_at;
                    return (
                      <tr
                        key={p.id}
                        className="hover:bg-[#F5F4FB] transition-colors"
                      >
                        <td className="px-4 py-3 text-xs text-[#6B7280] whitespace-nowrap">
                          {date
                            ? new Date(date).toLocaleString("en-IN", {
                                dateStyle: "medium",
                                timeStyle: "short",
                              })
                            : "–"}
                        </td>
                        <td className="px-4 py-3 font-mono text-xs text-[#6C63FF]">
                          #{p.order_id}
                        </td>
                        <td className="px-4 py-3">
                          <div>
                            <p className="font-medium text-[#1A1A2E] leading-tight">
                              {p.user_name}
                            </p>
                            <p className="text-[10px] text-[#9CA3AF]">
                              {p.user_email}
                            </p>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-[#4A4A60]">
                          {p.service_name || "–"}
                        </td>
                        <td className="px-4 py-3 font-mono font-semibold text-[#1A1A2E] whitespace-nowrap">
                          {formatINRFull(p.amount)}
                        </td>
                        <td className="px-4 py-3 text-xs text-[#6B7280]">
                          {p.payment_method || "–"}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`inline-flex items-center gap-1 text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.color}`}
                          >
                            <StatusIcon className="h-3 w-3" />
                            {meta.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 font-mono text-[10px] text-[#9CA3AF] truncate max-w-[160px]">
                          {p.payment_id || "–"}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between mt-4">
          <button
            onClick={exportCsv}
            disabled={list.payments.length === 0}
            className="flex items-center gap-1.5 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/10 px-3 py-2 rounded-lg disabled:opacity-40 transition-colors"
          >
            <Download className="h-3.5 w-3.5" />
            Export current page
          </button>

          {list.pages > 1 && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-body text-[#6B7280]">
                {(page - 1) * PAGE_SIZE + 1}–
                {Math.min(page * PAGE_SIZE, list.total)} of {list.total}
              </span>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
              >
                <ChevronLeft className="h-3.5 w-3.5" /> Prev
              </button>
              <span className="text-xs font-body text-[#6B7280]">
                Page {page} / {list.pages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(list.pages, p + 1))}
                disabled={page === list.pages}
                className="flex items-center gap-1 text-xs font-body px-3 py-1.5 border border-[#E8E6F8] rounded-lg hover:bg-[#F5F4FB] disabled:opacity-40"
              >
                Next <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function KPISection({ insights, onRefresh }) {
  const p = insights.periods;
  const m = insights.mom;

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <IndianRupee className="h-4 w-4 text-[#6C63FF]" />
          <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">
            Sales Overview
          </h3>
        </div>
        <button
          onClick={onRefresh}
          className="flex items-center gap-1.5 text-xs font-body text-[#6B7280] hover:text-[#6C63FF] px-2 py-1 rounded-lg hover:bg-[#F5F4FB] transition-colors"
        >
          <RefreshCw className="h-3 w-3" />
          Refresh
        </button>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPICard
          label="Today"
          value={formatINR(p.today)}
          accent="bg-amber-50 text-amber-700"
        />
        <KPICard
          label="Last 7d"
          value={formatINR(p.week)}
          accent="bg-sky-50 text-sky-700"
        />
        <KPICard
          label="This Month"
          value={formatINR(p.month)}
          accent="bg-[#6C63FF]/10 text-[#6C63FF]"
          footer={
            m.delta_pct == null ? (
              <span className="text-[10px] text-[#9CA3AF]">No prior month</span>
            ) : (
              <span
                className={`text-[10px] inline-flex items-center gap-0.5 ${m.delta_pct >= 0 ? "text-green-600" : "text-red-500"}`}
              >
                {m.delta_pct >= 0 ? (
                  <TrendingUp className="h-3 w-3" />
                ) : (
                  <TrendingDown className="h-3 w-3" />
                )}
                {Math.abs(m.delta_pct).toFixed(1)}% vs last month
              </span>
            )
          }
        />
        <KPICard
          label="Quarter"
          value={formatINR(p.quarter)}
          accent="bg-violet-50 text-violet-700"
        />
        <KPICard
          label="Half Year"
          value={formatINR(p.half_year)}
          accent="bg-emerald-50 text-emerald-700"
        />
        <KPICard
          label="Year"
          value={formatINR(p.year)}
          accent="bg-indigo-50 text-indigo-700"
        />
        <KPICard
          label="All Time"
          value={formatINR(p.all_time)}
          accent="bg-pink-50 text-pink-700"
        />
      </div>
    </div>
  );
}

function ChartsSection({
  insights,
  granularity,
  setGranularity,
  series,
  statusPie,
}) {
  const granOptions = [
    { value: "monthly", label: "Monthly" },
    { value: "quarterly", label: "Quarterly" },
    { value: "half_yearly", label: "Half-yearly" },
    { value: "yearly", label: "Yearly" },
  ];

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Revenue trend */}
      <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
          <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">
            Revenue Trend
          </h3>
          <div className="inline-flex border border-[#E8E6F8] rounded-lg overflow-hidden text-xs font-body">
            {granOptions.map((o) => (
              <button
                key={o.value}
                onClick={() => setGranularity(o.value)}
                className={`px-3 py-1.5 transition-colors ${granularity === o.value ? "bg-[#6C63FF] text-white" : "bg-white text-[#6B7280] hover:bg-[#F5F4FB]"}`}
              >
                {o.label}
              </button>
            ))}
          </div>
        </div>

        {series.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-12">
            No revenue data for this range
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <BarChart
              data={series}
              margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#F0EEF9" />
              <XAxis dataKey="label" tick={{ fontSize: 11, fill: "#6B7280" }} />
              <YAxis
                tick={{ fontSize: 11, fill: "#6B7280" }}
                tickFormatter={formatINR}
              />
              <Tooltip
                contentStyle={{
                  borderRadius: 8,
                  border: "1px solid #E8E6F8",
                  fontSize: 12,
                }}
                formatter={(v, name) =>
                  name === "revenue"
                    ? [formatINRFull(v), "Revenue"]
                    : [v, "Payments"]
                }
              />
              <Bar dataKey="revenue" fill="#6C63FF" radius={[6, 6, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {/* Status breakdown */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <h3 className="text-sm font-heading font-bold text-[#1A1A2E] mb-4">
          Payment Status
        </h3>
        {statusPie.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-12">
            No payments yet
          </p>
        ) : (
          <>
            <ResponsiveContainer width="100%" height={200}>
              <PieChart>
                <Pie
                  data={statusPie}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={45}
                  outerRadius={75}
                  paddingAngle={2}
                >
                  {statusPie.map((_, i) => (
                    <Cell key={i} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E8E6F8",
                    fontSize: 12,
                  }}
                />
                <Legend wrapperStyle={{ fontSize: 11 }} />
              </PieChart>
            </ResponsiveContainer>
            <div className="mt-3 pt-3 border-t border-[#F0EEF9] text-xs font-body text-[#6B7280] flex items-center justify-between">
              <span>Success Rate</span>
              <span className="font-semibold text-[#1A1A2E]">
                {insights.counts.success_rate}%
              </span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function SecondaryRow({ insights }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Top services */}
      <div className="lg:col-span-2 bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <h3 className="text-sm font-heading font-bold text-[#1A1A2E] mb-4">
          Top Services by Revenue
        </h3>
        {insights.top_services.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-8">
            No completed payments yet
          </p>
        ) : (
          <div className="space-y-2">
            {insights.top_services.map((s, i) => {
              const max = insights.top_services[0].revenue || 1;
              const pct = (s.revenue / max) * 100;
              return (
                <div
                  key={s.name + i}
                  className="flex items-center gap-3 text-sm font-body"
                >
                  <span className="font-mono text-[10px] text-[#9CA3AF] w-6">
                    {i + 1}.
                  </span>
                  <span className="w-44 truncate text-[#1A1A2E]">
                    {s.name || "Unknown"}
                  </span>
                  <div className="flex-1 bg-[#F5F4FB] rounded-full h-2 overflow-hidden">
                    <div
                      className="h-full bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] rounded-full"
                      style={{ width: `${pct}%` }}
                    />
                  </div>
                  <span className="font-mono text-xs text-[#1A1A2E] w-24 text-right">
                    {formatINR(s.revenue)}
                  </span>
                  <span className="text-[10px] text-[#9CA3AF] w-12 text-right">
                    ×{s.count}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Recent activity */}
      <div className="bg-white rounded-2xl border border-[#E8E6F8] shadow-sm p-5">
        <h3 className="text-sm font-heading font-bold text-[#1A1A2E] mb-4">
          Recent Completed
        </h3>
        {insights.recent.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-sm font-body py-8">
            No activity
          </p>
        ) : (
          <div className="space-y-3">
            {insights.recent.map((r) => (
              <div
                key={r.id}
                className="flex items-start gap-3 pb-3 border-b border-[#F0EEF9] last:border-0 last:pb-0"
              >
                <div className="w-8 h-8 rounded-full bg-green-100 flex items-center justify-center flex-shrink-0">
                  <ArrowUpRight className="h-3.5 w-3.5 text-green-600" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-[#1A1A2E] truncate">
                    {r.user_name}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF] truncate">
                    {r.service_name || "–"} · #{r.order_id}
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-sm font-mono font-semibold text-[#1A1A2E]">
                    {formatINR(r.amount)}
                  </p>
                  <p className="text-[10px] text-[#9CA3AF] inline-flex items-center gap-0.5">
                    <Calendar className="h-2.5 w-2.5" />
                    {r.paid_at
                      ? new Date(r.paid_at).toLocaleDateString("en-IN", {
                          day: "numeric",
                          month: "short",
                        })
                      : "–"}
                  </p>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function ServiceCardsGrid({ insights }) {
  const rows = insights.service_breakdown || [];
  const [openServiceId, setOpenServiceId] = useState(null);
  const totalRevenue = rows.reduce((s, r) => s + (r.revenue || 0), 0);

  const exportCsv = () => {
    const header = [
      "Service",
      "Type",
      "Price",
      "Orders",
      "Customers",
      "Completed",
      "Pending",
      "Failed",
      "Revenue",
      "Avg Ticket",
      "% of Total",
      "This Month",
      "Last Month",
      "MoM Δ %",
    ];
    const rowsCsv = rows.map((r) => [
      r.name || "Unknown",
      r.type || "–",
      r.price_label || "–",
      r.orders_count,
      r.unique_customers,
      r.completed_count,
      r.pending_count,
      r.failed_count,
      r.revenue,
      r.avg_ticket.toFixed(2),
      totalRevenue ? ((r.revenue / totalRevenue) * 100).toFixed(2) : "0",
      r.revenue_this_month,
      r.revenue_last_month,
      r.mom_delta_pct == null ? "" : r.mom_delta_pct,
    ]);
    const csv = [header, ...rowsCsv]
      .map((r) =>
        r.map((c) => `"${String(c ?? "").replace(/"/g, '""')}"`).join(","),
      )
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `service-revenue-breakdown.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
        <div>
          <h3 className="text-sm font-heading font-bold text-[#1A1A2E]">
            Service-wise Sales Breakdown
          </h3>
          <p className="text-[11px] text-[#9CA3AF] font-body mt-0.5">
            Click any card for the complete sales detail of that service
          </p>
        </div>
        <button
          onClick={exportCsv}
          disabled={rows.length === 0}
          className="flex items-center gap-1.5 text-xs font-body text-[#6C63FF] border border-[#6C63FF]/30 hover:bg-[#6C63FF]/10 px-3 py-1.5 rounded-lg disabled:opacity-40 transition-colors"
        >
          <Download className="h-3.5 w-3.5" />
          Export CSV
        </button>
      </div>

      {rows.length === 0 ? (
        <div className="bg-white rounded-2xl border border-[#E8E6F8] p-12 text-center">
          <p className="text-sm text-[#9CA3AF] font-body">
            No services configured yet
          </p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {rows.map((r) => (
            <ServiceCard
              key={r.service_id}
              row={r}
              totalRevenue={totalRevenue}
              onClick={() => setOpenServiceId(r.service_id)}
            />
          ))}
        </div>
      )}

      {openServiceId && (
        <ServiceDetailModal
          serviceId={openServiceId}
          onClose={() => setOpenServiceId(null)}
        />
      )}
    </div>
  );
}

function ServiceCard({ row, totalRevenue, onClick }) {
  const pct = totalRevenue ? (row.revenue / totalRevenue) * 100 : 0;
  const hasData = row.orders_count > 0;
  return (
    <button
      onClick={onClick}
      className={`group text-left bg-white rounded-2xl border border-[#E8E6F8] hover:border-[#6C63FF]/40 hover:shadow-md p-4 transition-all ${hasData ? "" : "opacity-95"}`}
    >
      <div className="flex items-start justify-between gap-2 mb-3">
        <div className="min-w-0">
          <p className="text-sm font-heading font-bold text-[#1A1A2E] leading-tight truncate group-hover:text-[#6C63FF]">
            {row.name}
          </p>
          <p className="text-[10px] text-[#9CA3AF] font-body mt-0.5 truncate">
            {row.price_label || "–"} {row.type ? `· ${row.type}` : ""}
          </p>
        </div>
        {row.mom_delta_pct != null && (
          <span
            className={`inline-flex items-center gap-0.5 text-[10px] font-mono px-1.5 py-0.5 rounded-full ${row.mom_delta_pct >= 0 ? "bg-green-50 text-green-600" : "bg-red-50 text-red-500"}`}
          >
            {row.mom_delta_pct >= 0 ? (
              <TrendingUp className="h-2.5 w-2.5" />
            ) : (
              <TrendingDown className="h-2.5 w-2.5" />
            )}
            {Math.abs(row.mom_delta_pct).toFixed(1)}%
          </span>
        )}
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 mb-3">
        <div>
          <p className="text-[10px] uppercase tracking-wide font-body text-[#9CA3AF]">
            Revenue
          </p>
          <p className="text-base font-heading font-bold text-[#6C63FF] leading-tight">
            {formatINR(row.revenue)}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-body text-[#9CA3AF]">
            Orders
          </p>
          <p className="text-base font-heading font-bold text-[#1A1A2E] leading-tight">
            {row.orders_count}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-body text-[#9CA3AF]">
            Avg Ticket
          </p>
          <p className="text-sm font-heading font-semibold text-[#1A1A2E] leading-tight">
            {row.completed_count ? formatINR(row.avg_ticket) : "–"}
          </p>
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-wide font-body text-[#9CA3AF]">
            Customers
          </p>
          <p className="text-sm font-heading font-semibold text-[#1A1A2E] leading-tight">
            {row.unique_customers}
          </p>
        </div>
      </div>

      <div className="flex items-center gap-2">
        <div className="flex-1 h-1.5 bg-[#F5F4FB] rounded-full overflow-hidden">
          <div
            className="h-full bg-gradient-to-r from-[#6C63FF] to-[#8B7CFF] rounded-full"
            style={{ width: `${Math.min(pct, 100)}%` }}
          />
        </div>
        <span className="font-mono text-[10px] text-[#6B7280] w-10 text-right">
          {pct.toFixed(1)}%
        </span>
      </div>

      <div className="mt-3 pt-3 border-t border-[#F0EEF9] flex items-center justify-between text-[10px] font-body">
        <div className="flex items-center gap-2">
          <span className="text-green-600">{row.completed_count} paid</span>
          {row.pending_count > 0 && (
            <span className="text-amber-600">{row.pending_count} pending</span>
          )}
          {row.failed_count > 0 && (
            <span className="text-red-500">{row.failed_count} failed</span>
          )}
        </div>
        <span className="text-[#6C63FF] group-hover:translate-x-0.5 transition-transform inline-flex items-center gap-0.5">
          Details <ArrowUpRight className="h-3 w-3" />
        </span>
      </div>
    </button>
  );
}

function ServiceDetailModal({ serviceId, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError("");
      try {
        const { data } = await API.get(`/admin/payments/services/${serviceId}`);
        if (!cancelled) setData(data);
      } catch (e) {
        if (!cancelled)
          setError(
            e.response?.data?.detail || e.message || "Failed to load detail",
          );
      }
      if (!cancelled) setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [serviceId]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-2xl w-full max-w-5xl max-h-[90vh] overflow-y-auto"
      >
        <div className="sticky top-0 bg-white border-b border-[#E8E6F8] px-6 py-4 flex items-center justify-between z-10">
          <div className="min-w-0">
            <h3 className="text-base font-heading font-bold text-[#1A1A2E] truncate">
              {data?.name || serviceId}
            </h3>
            <p className="text-[11px] text-[#9CA3AF] font-body mt-0.5">
              {data?.price_label || "–"}
              {data?.type ? ` · ${data.type}` : ""}
              {" · Service ID: "}
              <span className="font-mono">{serviceId}</span>
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-1.5 rounded-lg hover:bg-[#F5F4FB] text-[#6B7280]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <div className="w-7 h-7 border-2 border-[#6C63FF] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : error ? (
            <p className="text-sm text-red-500 font-body text-center py-12">
              {error}
            </p>
          ) : data ? (
            <ServiceDetailContent data={data} />
          ) : null}
        </div>
      </div>
    </div>
  );
}

function ServiceDetailContent({ data }) {
  const { totals, monthly, status_breakdown, top_customers, recent_payments } =
    data;

  const monthlySeries = monthly.map((r) => ({
    label: formatBucketLabel(r.bucket, "monthly"),
    revenue: r.revenue,
    count: r.count,
  }));

  return (
    <div className="space-y-5">
      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        <KPICard
          label="Revenue"
          value={formatINR(totals.revenue)}
          accent="bg-[#6C63FF]/10 text-[#6C63FF]"
        />
        <KPICard
          label="Orders"
          value={totals.orders_count}
          accent="bg-sky-50 text-sky-700"
        />
        <KPICard
          label="Customers"
          value={totals.unique_customers}
          accent="bg-violet-50 text-violet-700"
        />
        <KPICard
          label="Paid"
          value={totals.completed_count}
          accent="bg-green-50 text-green-700"
        />
        <KPICard
          label="Pending"
          value={totals.pending_count}
          accent="bg-amber-50 text-amber-700"
        />
        <KPICard
          label="Failed"
          value={totals.failed_count}
          accent="bg-red-50 text-red-600"
        />
        <KPICard
          label="Avg Ticket"
          value={totals.completed_count ? formatINR(totals.avg_ticket) : "–"}
          accent="bg-pink-50 text-pink-700"
        />
      </div>

      {/* Monthly chart + status pie */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 bg-[#F9F8FD] rounded-xl border border-[#E8E6F8] p-4">
          <h4 className="text-xs font-heading font-bold text-[#1A1A2E] mb-3">
            Monthly Revenue (last 12 months)
          </h4>
          {monthlySeries.length === 0 ? (
            <p className="text-center text-[#9CA3AF] text-xs font-body py-12">
              No completed payments yet for this service
            </p>
          ) : (
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={monthlySeries}
                margin={{ top: 5, right: 10, left: 0, bottom: 5 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#F0EEF9" />
                <XAxis
                  dataKey="label"
                  tick={{ fontSize: 10, fill: "#6B7280" }}
                />
                <YAxis
                  tick={{ fontSize: 10, fill: "#6B7280" }}
                  tickFormatter={formatINR}
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 8,
                    border: "1px solid #E8E6F8",
                    fontSize: 12,
                  }}
                  formatter={(v) => [formatINRFull(v), "Revenue"]}
                />
                <Bar dataKey="revenue" fill="#6C63FF" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-[#F9F8FD] rounded-xl border border-[#E8E6F8] p-4">
          <h4 className="text-xs font-heading font-bold text-[#1A1A2E] mb-3">
            Order Status
          </h4>
          {status_breakdown.length === 0 ? (
            <p className="text-center text-[#9CA3AF] text-xs font-body py-12">
              No orders yet
            </p>
          ) : (
            <div className="space-y-2">
              {status_breakdown.map((s) => (
                <div
                  key={s.status}
                  className="flex items-center justify-between text-xs font-body"
                >
                  <span className="capitalize text-[#6B7280]">{s.status}</span>
                  <span className="font-heading font-bold text-[#1A1A2E]">
                    {s.count}
                  </span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Top customers */}
      <div className="bg-white rounded-xl border border-[#E8E6F8] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E8E6F8]">
          <h4 className="text-xs font-heading font-bold text-[#1A1A2E]">
            Top Customers (LTV)
          </h4>
        </div>
        {top_customers.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-xs font-body py-8">
            No customers yet
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-body">
              <thead>
                <tr className="bg-[#F5F4FB] text-[#6B7280]">
                  <th className="px-3 py-2 text-left font-heading">#</th>
                  <th className="px-3 py-2 text-left font-heading">Customer</th>
                  <th className="px-3 py-2 text-right font-heading">Orders</th>
                  <th className="px-3 py-2 text-right font-heading">Revenue</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {top_customers.map((c, i) => (
                  <tr key={c.id}>
                    <td className="px-3 py-2 text-[#9CA3AF] font-mono">
                      {i + 1}
                    </td>
                    <td className="px-3 py-2">
                      <p className="font-medium text-[#1A1A2E]">{c.name}</p>
                      <p className="text-[10px] text-[#9CA3AF]">{c.email}</p>
                    </td>
                    <td className="px-3 py-2 text-right font-mono">
                      {c.orders_count}
                    </td>
                    <td className="px-3 py-2 text-right font-mono font-semibold text-[#1A1A2E]">
                      {formatINRFull(c.revenue)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Recent payments */}
      <div className="bg-white rounded-xl border border-[#E8E6F8] overflow-hidden">
        <div className="px-4 py-3 border-b border-[#E8E6F8]">
          <h4 className="text-xs font-heading font-bold text-[#1A1A2E]">
            Recent Payments (last 25)
          </h4>
        </div>
        {recent_payments.length === 0 ? (
          <p className="text-center text-[#9CA3AF] text-xs font-body py-8">
            No payments yet
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-xs font-body">
              <thead>
                <tr className="bg-[#F5F4FB] text-[#6B7280]">
                  <th className="px-3 py-2 text-left font-heading">Date</th>
                  <th className="px-3 py-2 text-left font-heading">Order</th>
                  <th className="px-3 py-2 text-left font-heading">User</th>
                  <th className="px-3 py-2 text-right font-heading">Amount</th>
                  <th className="px-3 py-2 text-left font-heading">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E8E6F8]">
                {recent_payments.map((p) => {
                  const meta = STATUS_META[p.status] || {
                    label: p.status,
                    color: "bg-gray-100 text-gray-600",
                  };
                  const date = p.paid_at || p.created_at;
                  return (
                    <tr key={p.id}>
                      <td className="px-3 py-2 text-[#6B7280] whitespace-nowrap">
                        {date
                          ? new Date(date).toLocaleDateString("en-IN", {
                              day: "numeric",
                              month: "short",
                              year: "2-digit",
                            })
                          : "–"}
                      </td>
                      <td className="px-3 py-2 font-mono text-[#6C63FF]">
                        #{p.order_id}
                      </td>
                      <td className="px-3 py-2">
                        <p className="text-[#1A1A2E]">{p.user_name}</p>
                        <p className="text-[10px] text-[#9CA3AF]">
                          {p.user_email}
                        </p>
                      </td>
                      <td className="px-3 py-2 text-right font-mono font-semibold text-[#1A1A2E]">
                        {formatINRFull(p.amount)}
                      </td>
                      <td className="px-3 py-2">
                        <span
                          className={`inline-block text-[10px] px-2 py-0.5 rounded-full font-medium ${meta.color}`}
                        >
                          {meta.label}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

function KPICard({ label, value, accent, footer }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E6F8] p-3 flex flex-col gap-1">
      <span
        className={`inline-block text-[10px] font-body font-semibold uppercase tracking-wide px-2 py-0.5 rounded-full self-start ${accent}`}
      >
        {label}
      </span>
      <p className="text-lg font-heading font-bold text-[#1A1A2E] leading-tight">
        {value}
      </p>
      {footer && <div>{footer}</div>}
    </div>
  );
}

function MiniStat({ label, value, sub, tone }) {
  return (
    <div className="bg-white rounded-xl border border-[#E8E6F8] p-3">
      <p className="text-[10px] uppercase tracking-wide font-body text-[#9CA3AF]">
        {label}
      </p>
      <p
        className={`text-lg font-heading font-bold leading-tight ${tone || "text-[#1A1A2E]"}`}
      >
        {value}
      </p>
      <p className="text-[10px] text-[#9CA3AF] font-body">{sub}</p>
    </div>
  );
}
